import { Permissions } from '../../../../models/enums/permissions';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch } from 'react-router';
import { NavRibbon, NavRibbonLink } from '../../../../components/nav-ribbon/NavRibbon';
import { PageContent } from '../../../../components/page-components/PageContent';
import { PageHeading } from '../../../../components/page-components/PageHeading';
import { hasPermission } from '../../../../containers/authorization-helpers/has-permission';
import { HasPermission } from '../../../../containers/authorization-helpers/HasPermission';
import { RequirePermission } from '../../../../containers/authorization-helpers/RoutePermission';

import { IApplicationState } from '../../../../store/IApplicationState';
import { PageNames, Routes } from '../../../../utils/app-constants';
import { AppUtils } from '../../../../utils/app-utils';
import { updateSelectedFleetTab } from '../../../../transportation/actions';
import { EditAssetScene } from './AssetEdit';
import { AssetSetupScene } from './AssetSetup';
import { EditDriverScene } from './DriverEdit';
import { DriverSetupScene } from './DriverSetup';

interface IButtonState {
    permission : Permissions;
    path: string;
    label: string;
}

const driverBtnState = {
    permission:  Permissions.ShippingFleetDriversCreate,
    path: Routes.SHIPPING_FLEET_DRIVERS_EDIT,
    label: 'Create Driver'
};

const assetBtnState = {
    permission: Permissions.ShippingFleetAssetsCreate,
    path: Routes.SHIPPING_FLEET_ASSETS_EDIT,
    label: 'Create Asset'
};

export const FleetSetupScene = (props : IBaseSceneProps) => {

    const dispatch                              = useDispatch();
    const storedPath                            = useSelector((state : IApplicationState) => state.transportation.selectedFleetTab);
    const [currentLocation, setCurrentLocation] = React.useState('');
    const [buttonState, setButtonState]         = React.useState<IButtonState>(driverBtnState);
    const [driverFlag, setDriverFlag]           = React.useState(0);
    const [assetFlag, setAssetFlag]             = React.useState(0);

    React.useEffect(() => {
        getButton();
        _persistTab();
    }, [props.location]);

    const isDrivers = () => {
        return document.location.pathname.indexOf(Routes.SHIPPING_FLEET_DRIVERS_EDIT) === -1 &&
               document.location.pathname.indexOf(Routes.SHIPPING_FLEET_DRIVERS) === 0;
    };

    const isAssets = () => {
        return document.location.pathname.indexOf(Routes.SHIPPING_FLEET_ASSETS_EDIT) === -1 &&
               document.location.pathname.indexOf(Routes.SHIPPING_FLEET_ASSETS) === 0;
    };

    const getButton = () => {

        let state : IButtonState = {} as unknown as IButtonState;
        if (isDrivers()) {
            state = driverBtnState;
        }
        else if (isAssets()) {
            state = assetBtnState;
        }

        setButtonState(state);
    };

    const onButtonClick = () => {
        if (buttonState.label === 'Create Driver') {
            setDriverFlag(driverFlag + 1);
        }
        else if (buttonState.label === 'Create Asset') {
            setAssetFlag(assetFlag + 1);
        }
    };

    const reset = () => {
        setDriverFlag(0);
        setAssetFlag(0);
    };

    const _persistTab = () => {

        setCurrentLocation(props.location.pathname);
        
        let p = '';
        if (document.location.pathname.endsWith(Routes.SHIPPING_FLEET_ASSETS)) {            
            p = Routes.SHIPPING_FLEET_ASSETS;
        }
        else if (document.location.pathname.endsWith(Routes.SHIPPING_FLEET_DRIVERS)) {
            p = Routes.SHIPPING_FLEET_DRIVERS;
        }
        else if (storedPath) {
            p = storedPath;
        }

        dispatch(updateSelectedFleetTab(p));
    };

    /**
     * This handles checking the current user permissions to determine what the default 
     * path should be
     */
    const getRedirectPath = () => {

        const dPerm = Permissions.ShippingFleetDriversView;
        const aPerm = Permissions.ShippingFleetAssetsView;

        if (hasPermission({ requiredPermissions: [dPerm]})) {
            return Routes.SHIPPING_FLEET_DRIVERS;
        }
        else if (hasPermission({ requiredPermissions: [aPerm]})) {
            return Routes.SHIPPING_FLEET_ASSETS;
        }

        return '/';
    };

    return <PageContent pageName={PageNames.TRANSPORTATION_FLEET}>
                <PageHeading>
                    {
                        !AppUtils.IsEmpty(buttonState)
                            ? <HasPermission requiredPermissions={[
                                buttonState.permission
                            ]}>
                                <button className="btn new attention" onClick={ onButtonClick }>
                                    { buttonState.label }
                                </button>
                            </HasPermission>
                            : null
                    }                 
                </PageHeading>

                <NavRibbon links={[
                    new NavRibbonLink(
                        Routes.SHIPPING_FLEET_DRIVERS, 
                        'la-users',
                        'Drivers',
                        [Permissions.ShippingFleetDriversView]
                    ),
                    new NavRibbonLink(
                        Routes.SHIPPING_FLEET_ASSETS,
                        'la-truck',
                        'Assets',
                        [Permissions.ShippingFleetAssetsView]
                    )
                ]} />

            <Switch>

                <Route
                    key="transportation-fleet-drivers"
                    exact={true}
                    path={Routes.SHIPPING_FLEET_DRIVERS}
                    render={
                        RequirePermission(() =>
                            <DriverSetupScene
                                resetFlag={ reset }
                                createFlag={ driverFlag } 
                            />,
                            [
                                Permissions.ShippingFleetDriversView
                            ]
                        )
                    }
                />

                <Route
                    key="transportation-fleet-drivers-edit"
                    exact={true}
                    path={ Routes.SHIPPING_FLEET_DRIVERS_EDIT_ROUTE }
                    render={
                        RequirePermission(
                            EditDriverScene, 
                            [ 
                                Permissions.ShippingFleetDriversView
                            ]
                        )
                    }
                />   

                <Route
                    key="transportation-fleet-assets"
                    exact={true}
                    path={Routes.SHIPPING_FLEET_ASSETS}
                    render={
                        RequirePermission(() =>
                            <AssetSetupScene
                                resetFlag={ reset }
                                createFlag={ assetFlag } 
                            />,
                            [
                                Permissions.ShippingFleetAssetsView
                            ]
                        )
                    }
                />   

                <Route
                    key="transportation-fleet-assets-edit"
                    exact={true}
                    path={ Routes.SHIPPING_FLEET_ASSETS_EDIT_ROUTE }
                    render={
                        RequirePermission(
                            EditAssetScene, 
                            [ 
                                Permissions.ShippingFleetAssetsView
                            ]
                        )
                    }
                />   

                <Redirect
                    key="transportation-fleet-redirect"
                    exact={true}
                    from={ Routes.SHIPPING_FLEET }
                    to={ storedPath ? storedPath : getRedirectPath() } />

            </Switch>
        </PageContent>;
};
